import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom';
import { httpService } from '../../services/http-service';
import { newHttpService } from '../../services/new-http-service';
import AuditControlTable from '../../cmps/audit-control/audit-control-table';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/configure-store';
import {getAuditControlsByCompanyNameQuery} from '../../cmps/util-cmps/graphql-queries/audit-controls-query'
import { SelectCompanyPrompt } from '../../cmps/general/select-company-prompt'
import { AddAuditControlFormPage } from '../forms/add-audit-control-form-page'
import { AuditControlEditModal } from '../../cmps/audit-control/audit-control-edit-modal'
import { BatchEditConsolidationModal } from '../../cmps/audit-control/batch-edit-consolidation-modal'
import { Spinner } from '../../cmps/util-cmps/spinner'
import { useCachedFetch } from '../../hooks/use-cached-fetch'


const currentYear = new Date().getFullYear()

export const AuditControlPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [cloneSource, setCloneSource] = useState<any>(null)
  const [auditControlToEdit, setAuditControlToEdit] = useState<any>(null)
  const [batchEditControls, setBatchEditControls] = useState<any[] | null>(null)
  const [selectedYear, setSelectedYear] = useState(currentYear)
  const onlyProblematic = searchParams.get('health') === 'unhealthy'
  const setOnlyProblematic = (val: boolean | ((v: boolean) => boolean)) => {
    const next = typeof val === 'function' ? val(onlyProblematic) : val
    setSearchParams(prev => {
      const p = new URLSearchParams(prev)
      if (next) p.set('health', 'unhealthy')
      else p.delete('health')
      return p
    }, { replace: true })
  }
  const companyName = useSelector((stateModule: RootState) => stateModule.companyName.value)

  const { data, isLoading, hasError, refetch } = useCachedFetch(
    companyName ? `audit-controls:${companyName}` : null,
    async () => {
      const [acResult, handlerResult] = await Promise.all([
        httpService.graphql(companyName, getAuditControlsByCompanyNameQuery(companyName)),
        newHttpService.get('/api/backoffice/last-successful-runs', { company_name: companyName }).catch(() => []),
      ])
      const acData = acResult.auditControlsByCompanyName || []
      const runsMap: Record<number, string> = {}
      for (const record of handlerResult) {
        runsMap[record.audit_control_id] = record.created_at
      }
      return { acData, runsMap }
    }
  )

  const auditControlData = data?.acData || []
  const lastSuccessfulRuns = data?.runsMap || {}

  // Extract available years from data, always include current year
  const availableYears = useMemo(() => {
    const years = new Set<number>([currentYear])
    auditControlData.forEach((ac: any) => {
      if (ac.audit?.year) years.add(ac.audit.year)
    })
    return Array.from(years).sort((a, b) => b - a)
  }, [auditControlData])

  const filteredData = useMemo(() =>
    auditControlData.filter((ac: any) => ac.audit?.year === selectedYear),
    [auditControlData, selectedYear]
  )

  const openEditModal = (auditControl: any) => {
    setAuditControlToEdit(auditControl)
  }

  const closeEditModal = () => {
    setAuditControlToEdit(null)
  }

  const handleEditSuccess = () => {
    closeEditModal()
    refetch()
  }

  if (!companyName) {
    return <div className="tablePageContainer">
      <SelectCompanyPrompt />
    </div>
  }

  return <div className="tablePageContainer">
    <div className="page-header">
      <p className="tableSubtitle">Monitor system-wide compliance workflows, review execution logs, and manage control exceptions.</p>
      <div className="page-header-actions">
        <div className="year-filter-group">
          <span className="year-filter-label">Audit Year</span>
          <select
            className="year-filter-select"
            value={selectedYear}
            onChange={e => setSelectedYear(Number(e.target.value))}
          >
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
        <label className="toggle-switch-label">
          <span className="toggle-switch-text">Unhealthy Only</span>
          <span className={`toggle-switch ${onlyProblematic ? 'active' : ''}`} onClick={() => setOnlyProblematic(v => !v)}>
            <span className="toggle-switch-knob" />
          </span>
        </label>
        <button className="add-btn" onClick={() => setIsCreateModalOpen(true)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Create Audit Control
        </button>
      </div>
    </div>

    {isLoading && <Spinner />}
    {!isLoading && !hasError && <AuditControlTable data={filteredData} companyName={companyName} lastSuccessfulRuns={lastSuccessfulRuns} onDataChange={refetch} onEditClick={openEditModal} onCloneClick={(ac) => { setCloneSource(ac); setIsCreateModalOpen(true) }} onBatchEditConsolidation={setBatchEditControls} onlyProblematic={onlyProblematic} />}
    {!isLoading && hasError && <div className="error-hint">Failed to load data. Try refreshing the page or re-logging in.</div>}

    {isCreateModalOpen && <AddAuditControlFormPage
      onClose={() => { setIsCreateModalOpen(false); setCloneSource(null) }}
      onSuccess={() => { setIsCreateModalOpen(false); setCloneSource(null); refetch() }}
      cloneFrom={cloneSource ? {
        sourceAcId: cloneSource.id,
        entityId: cloneSource.audit?.entity?.id ?? null,
        auditId: cloneSource.audit?.id ?? null,
        objectiveId: cloneSource.control?.objective?.id ?? null,
        mainSystemId: cloneSource.control?.mainSystem?.id ?? null,
        hrSystemId: cloneSource.control?.hrSystem?.id ?? null,
        ticketSystemId: cloneSource.control?.ticketSystem?.id ?? null,
        consolidationJson: cloneSource.control?.systemConsolidations
          ? (typeof cloneSource.control.systemConsolidations === 'string'
            ? cloneSource.control.systemConsolidations
            : JSON.stringify(cloneSource.control.systemConsolidations))
          : null,
        controlOwnerId: cloneSource.controlOwner?.id ?? null,
        companyControlId: cloneSource.control?.companyControlId ?? null,
      } : undefined}
    />}

    {auditControlToEdit && <AuditControlEditModal
      key={auditControlToEdit.id}
      companyName={companyName}
      auditControl={auditControlToEdit}
      onSuccess={handleEditSuccess}
      onClose={closeEditModal}
    />}

    {batchEditControls && batchEditControls.length > 0 && <BatchEditConsolidationModal
      selectedAuditControls={batchEditControls}
      companyName={companyName}
      onClose={() => setBatchEditControls(null)}
      onSuccess={() => { setBatchEditControls(null); refetch() }}
    />}
  </div>
}
